/**
 * HTML sanitization for the rich-text editor.
 *
 * Lives in its own module so the security-critical primitive can be
 * unit-tested without booting the Lexical-based editor component.
 */

import DOMPurify, { type Config as DomPurifyConfig } from 'dompurify';

/**
 * DOMPurify config for the rich-text editor.
 *
 * Allowlists basic formatting tags + `a` for links, with `class` and
 * `style` so font/color round-trip survives an export → import cycle.
 * Hoisted to module scope so it isn't re-created on every keystroke.
 */
export const SANITIZE_CONFIG: DomPurifyConfig = {
  ALLOWED_TAGS: [
    'p', 'br', 'span', 'div',
    'strong', 'b', 'em', 'i', 'u', 's', 'strike',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'a',
    'blockquote',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'dir'],
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ['target'],
  FORCE_BODY: true,
  RETURN_TRUSTED_TYPE: false,
};

/** Sanitize HTML on every editor import and export. Empty input → empty string. */
export const sanitizeHtml = (html: string): string =>
  html ? (DOMPurify.sanitize(html, SANITIZE_CONFIG) as unknown as string) : '';
